import * as React from 'react';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createGRN } from 'logic/permissions/GRN';
import { IfPermitted } from 'components/common';
import UserNotification from 'util/UserNotification';
import { MenuItem } from 'components/bootstrap';
import StringUtils from 'util/StringUtils';
import BulkActionsDropdown from 'components/common/EntityDataTable/BulkActionsDropdown';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';
import { COLLECTION_ENTITIES_QUERY_KEY } from 'collections/hooks/useCollectionEntities';
import { COLLECTION_CANDIDATES_QUERY_KEY } from 'collections/hooks/useCollectionCandidates';
import type { Entity, Collection } from 'collections/types';
import useCollectionEntityMutation from 'collections/hooks/useCollectionEntityMutation';

const getDescriptor = (count: number) => StringUtils.pluralize(count, 'item', 'items');

type Props = {
  collectionId: Collection['id'];
  selectedEntities: Array<Entity>;
};

const BulkActions = ({ collectionId, selectedEntities }: Props) => {
  const { bulkAddEntitiesMutation } = useCollectionEntityMutation(collectionId);
  const queryClient = useQueryClient();
  const { setSelectedEntities } = useSelectedEntities();
  const selectedItemsAmount = selectedEntities?.length;
  const refetchCollection = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId],
    });
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_CANDIDATES_QUERY_KEY, collectionId],
    });
  }, [queryClient, collectionId]);

  const onAdd = useCallback(() => {
    bulkAddEntitiesMutation
      .mutateAsync({ addedEntities: selectedEntities.map((entity) => createGRN(entity.type, entity.id)) })
      .then(({ failures }) => {
        if (failures?.length) {
          const notUpdatedCollectionEntityIds = failures.map(({ entity_id }) => entity_id);
          setSelectedEntities(notUpdatedCollectionEntityIds);
          UserNotification.error(
            `${notUpdatedCollectionEntityIds.length} out of ${selectedItemsAmount} selected ${getDescriptor(selectedItemsAmount)} could not be added.`,
          );
        } else {
          setSelectedEntities([]);
          UserNotification.success(
            `${selectedItemsAmount} ${getDescriptor(selectedItemsAmount)} ${StringUtils.pluralize(selectedItemsAmount, 'was', 'were')} added successfully.`,
            'Success',
          );
        }
      })
      .catch((error) => {
        UserNotification.error(`An error occurred while adding content. ${error}`);
      })
      .finally(() => {
        refetchCollection();
      });
  }, [bulkAddEntitiesMutation, refetchCollection, selectedEntities, selectedItemsAmount, setSelectedEntities]);

  return (
    <BulkActionsDropdown>
      <IfPermitted permissions={`collection_entities:edit:${collectionId}`}>
        <MenuItem onSelect={onAdd}>Add</MenuItem>
      </IfPermitted>
    </BulkActionsDropdown>
  );
};

export default BulkActions;
